
module Pki {
    import CertificateAttributes = EEX.McEc.Controls.CertificateDialog.CertificateAttributes;

    export class Store {
        private keyPair: forge.pki.KeyPair;
        private pkPem: string;
        private certificate: forge.pki.Certificate;

        constructor() {
            this.keyPair = {
                privateKey: null,
                publicKey: null
            };
            this.pkPem = null;
            this.certificate = null;
        }

        public getPkPem(): string {
            return this.pkPem;
        }
        
        public hasCertificate(): boolean {
            return this.certificate !== null;
        }

        public setCertificateFromFile(base64CertificateFile: string): void {
            const certificate = this.decodeCertificate(base64CertificateFile);
            if (!(certificate instanceof Error)) {
                this.setCertificate(certificate);
            }
        }

        public setCertificate(certificate: forge.pki.Certificate): void {
            this.certificate = certificate;
        }

        public createPrivateAndPublicKeyPair(callback: Action<Store>) {
            forge.pki.rsa.generateKeyPair({ bits: 2048, workerScript: "./Scripts/EEX.McEc/3rdParty/prime.worker.min.js" },
                (error, keyPair) => {
                    if (error) {
                        AppKitchen.logError(error.stack);
                    } else {
                        this.keyPair = keyPair;
                        if (callback) {
                            callback(this);
                        }
                    }
                });
            //var state = forge.rsa.createKeyPairGenerationState(2048);
            //var step = () => {
            //    if (!forge.rsa.stepKeyPairGenerationState(state, 100)) {
            //        setTimeout(step, 1);
            //    }
            //    else {
            //        console.log("rsa keypair generated");
            //        this.keyPair = {
            //            privateKey: state.keys.privateKey,
            //            publicKey: state.keys.publicKey
            //        };

            //        if (callback) {
            //            callback(this);
            //        }
            //    }
            //};

            //setTimeout(step);
        }

        public getPkcs12Format(pkcsPassword: string, includeCertificate: boolean = true): any {
            const pkcs = this.createPkcs12Wrapper(pkcsPassword, includeCertificate);

            if (!(pkcs instanceof Error)) {
                return this.encodePkcs12Format(pkcs);
            }
            return pkcs;
        }

        public getPrivateKeyPem(password: string): void {
            this.pkPem = forge.pki.encryptRsaPrivateKey(this.keyPair.privateKey, password, { legacy: true, algorithm: 'aes256' });
        }

        public setPrivateKeyPem(privateKeyPem: string, password: string): Error | boolean {
            try {
                this.keyPair.privateKey = forge.pki.decryptRsaPrivateKey(privateKeyPem, password);
                if (this.keyPair.privateKey === null) {
                    return new Error("Wrong password", ErrorType.EncryptedPrivateKeyInvalidPassword);
                }
            } catch (e) {
                return new Error(e, ErrorType.EncryptedPrivateKeyInvalid);
            }

            this.keyPair.publicKey = this.setRsaPublicKey(this.keyPair.privateKey as forge.pki.rsa.PrivateKey);

            if (!this.checkIfPrivateKeyFitToPublicKeyFromCertificate()) {
                return new Error("Private key not match with certificate", ErrorType.EncryptedPrivateKeyInvalidPrivateKeyNotMatchWithCertificate);
            }

            return true;
        }

        private setRsaPublicKey(privateKey: forge.pki.rsa.PrivateKey): forge.pki.PublicKey {
            return forge.pki.setRsaPublicKey(privateKey.n, privateKey.e);
        }

        public createCertificateRequest(certificateAttributes: CertificateAttributes, isServerCertificate: boolean): string | Error {
            var e;
            if (!this.keyPair) {
                return new Error("Key pair not available.", ErrorType.InvalidKeypair);
            }

            if (!this.keyPair.privateKey) {
                return new Error("Private key not available.", ErrorType.InvalidKeypair);
            }

            if (!this.keyPair.publicKey) {
                console.log("Recover public key from private key.");
                try {
                    this.keyPair.publicKey = this.setRsaPublicKey(this.keyPair.privateKey as forge.pki.rsa.PrivateKey);
                } catch (e) {
                    return new Error(e, ErrorType.InvalidKeypair);
                }
            }

            if (!certificateAttributes) {
                return new Error("Attributes for certificate request not set.", ErrorType.MissingCommonName);
            }

            if (!certificateAttributes.commonName) {
                return new Error("Common name for certificate request not set.", ErrorType.MissingCommonName);
            }


            var csr = forge.pki.createCertificationRequest();

            csr.publicKey = this.keyPair.publicKey;

            var subject = this.getSubject(certificateAttributes);
            csr.setSubject(subject);

            var extensions = this.getExtensions(isServerCertificate, certificateAttributes.commonName);
            

            // TODO forge code doesn't fit to type definition!
            //csr.setExtensions(extensions); should be like this
            (<any>csr).setAttributes(extensions);

            try {
                //csr.sign(this.keyPair.privateKey);
                //csr.sign(this.keyPair.privateKey, forge.md.sha1.create());
                csr.sign(this.keyPair.privateKey, forge.md.sha256.create());
            } catch (e) {
                return new Error(e, ErrorType.InvalidKeypair);
            }

            try {
                //if (csr.verify()) {
                    console.log('Certification request (CSR) verified.');
                //} else {
                //    throw new Error('Signature not verified.');
                //}
            } catch (err) {
                console.log('Certification request (CSR) verification failure: ' +
                    JSON.stringify(err, null, 2));
            }

            return forge.pki.certificationRequestToPem(csr);
        }

        private getExtensions(isServerCertificate: boolean, commonName: string): any[] {
            var extensions: any[] = [{
                name: 'keyUsage',
                keyCertSign: false,
                digitalSignature: true,
                nonRepudiation: false,
                keyEncipherment: true,
                dataEncipherment: false,
            }];

            if (isServerCertificate) {
                extensions.push({
                    name: 'extKeyUsage',
                    serverAuth: true,
                    clientAuth: false,
                    codeSigning: false,
                    emailProtection: false,
                    timeStamping: false,
                });
                extensions.push({
                    name: 'subjectAltName',
                    altNames: [{
                        type: 6, // URI
                        value: commonName
                    }]
                });
            } else {
                extensions.push({
                    name: 'extKeyUsage',
                    serverAuth: false,
                    clientAuth: true,
                    codeSigning: false,
                    emailProtection: false,
                    timeStamping: false
                });
            }

            let attributes = [{
                name: 'extensionRequest',
                extensions: extensions
            }];

            return attributes;
        }

        private getSubject(certificateAttributes: CertificateAttributes): forge.pki.CertificateField[] {

            var subject: forge.pki.CertificateField[] = [{
                name: 'commonName', // CN
                value: certificateAttributes.commonName,
            }];

            this.setAttributeWithName(subject, 'countryName', certificateAttributes.countryName); // C
            this.setAttributeWithName(subject, 'localityName', certificateAttributes.localityName); // L
            this.setAttributeWithName(subject, 'organizationName', certificateAttributes.organizationName); // O
            this.setAttributeWithShortName(subject, 'ST', certificateAttributes.stateName);
            this.setAttributeWithShortName(subject, 'OU', certificateAttributes.organizationalUnitName);

            return subject;
        }

        private setAttributeWithName(subject: forge.pki.CertificateField[], name: string, value: string): void {
            if (value) {
                subject.push({
                    name: name,
                    value: value,
                });
            }
        }

        private setAttributeWithShortName(subject: forge.pki.CertificateField[], shortName: string, value: string): any {
            if (value) {
                subject.push({
                    shortName: shortName,
                    value: value,
                });
            }
        }

        private decodeCertificate(certB46: string): forge.pki.Certificate | Error {
            if (!certB46) {
                return null;
                //return new Error("Encoded Certificate not available", ErrorType.MissingCertificate);
            }
            var error: Error;
            try {
                var lines = certB46.trim().split('\n');
                lines.splice(0, 1);
                lines.splice(lines.length - 1, 1);
                certB46 = lines.join('\n');
                var certDer2 = forge.util.decode64(certB46);
                var certAsn12 = forge.asn1.fromDer(certDer2);
                return forge.pki.certificateFromAsn1(certAsn12);

            } catch (e) {
                error = new Error(e.message, ErrorType.WrongCertificateEncoding);
            }
            try {
                var certDer = forge.util.decode64(certB46);
                var certAsn1 = forge.asn1.fromDer(certDer);
                return forge.pki.certificateFromAsn1(certAsn1);
            } catch (ex) {
                error = new Error(ex.message, ErrorType.WrongCertificateEncoding);
            }

            return error;
        }

        private encodePkcs12Format(p12Asn1: forge.asn1.Asn1): string {
            var p12Der = forge.asn1.toDer(p12Asn1).getBytes();
            return Store.toBase64(p12Der); 
        }

        public static toBase64(data: any) {
            return forge.util.encode64(data);
        }

        public checkIfPrivateKeyFitToPublicKeyFromCertificate(): boolean {
            return this.checkKeyPairWithCertificate(this.keyPair.privateKey, this.certificate);
        }

        private createPkcs12Wrapper(pkcsPassword = "", includeCertificate: boolean): forge.asn1.Asn1 | Error {
            if (!this.keyPair) {
                return new Error("Keypair not available.", ErrorType.InvalidKeypair);
            }

            if (!this.keyPair.privateKey) {
                return new Error("Private key not available.", ErrorType.InvalidKeypair);
            }

            if (includeCertificate && !this.hasCertificate()) {
                return new Error("Certificate missing!", ErrorType.MissingCertificate);
            }

            try {
                if (!this.checkIfPrivateKeyFitToPublicKeyFromCertificate()) {
                    return new Error("Certificate and private key doesn't match!", ErrorType.CertificateWrappingFailed);
                }
                const certificate = includeCertificate ? this.certificate : null;
                const algorithm: 'aes128' | 'aes192' | 'aes256' | '3des' = '3des';
                const result = forge.pkcs12.toPkcs12Asn1(this.keyPair.privateKey, certificate, pkcsPassword, { algorithm: algorithm });
                return result;
            } catch (e) {
                return new Error(e, ErrorType.CertificateWrappingFailed);
            }
        }

        private checkKeyPairWithCertificate(privateKey: forge.pki.PrivateKey, certificate: forge.pki.Certificate) {
            if (certificate === null || certificate === undefined) {
                return true;
            }
            return this.matchPrivateKeyToPublic(privateKey as forge.pki.rsa.PrivateKey, certificate.publicKey as forge.pki.rsa.PublicKey);
        }

        private matchPrivateKeyToPublic(privateKey: forge.pki.rsa.PrivateKey, publicKey: forge.pki.rsa.PublicKey): boolean {
            return this.equalsBitInteger(privateKey.e, publicKey.e) &&
                this.equalsBitInteger(privateKey.n, publicKey.n);
        }

        private equalsBitInteger(l: forge.jsbn.BigInteger, r: forge.jsbn.BigInteger): boolean {
            if (l.s !== r.s)
                return false;
            if (l.t !== r.t)
                return false;
            if (l.data.length !== r.data.length)
                return false;
            for (let i = 0; i <= l.data.length; i++)
                if (l.data[i] !== r.data[i])
                    return false;
            return true;
        }
    }
}
